import { translateUi } from "@i18n/config"; /**
 * Utility for generating storefront URLs consistently across the admin application.
 */

export interface StorefrontUrlOptions {
  path?: string;
  slug?: string | null;
  previewToken?: string | null;
  domain?: string | null;
  baseUrl?: string | null;
  locale?: string | null;
  planId?: string | null;
  storeStatus?: string | null;
  subscriptionStatus?: string | null;
}

export interface StorefrontPreviewUrlOptions {
  previewToken: string;
  redirectPath?: string;
  slug?: string | null;
  domain?: string | null;
  locale?: string | null;
  origin?: string | null;
}

export interface ResolvedStorefrontTarget {
  kind: "storefront" | "admin-billing" | "owner-billing" | "unavailable";
  path: string;
  url: string;
}

export type PublicStorefrontStore = {
  slug?: string | null;
  domain?: string | null;
  activeDomain?: string | null;
  storefrontDomain?: string | null;
  storefrontUrl?: string | null;
  baseUrl?: string | null;
  locale?: string | null;
  defaultLanguage?: string | null;
};

export type PublicStorefrontProduct = {
  id?: string | null;
  slug?: string | null;
  publicUrl?: string | null;
  status?: string | null;
  isActive?: boolean | null;
};

export type PublicStorefrontCategory = {
  id?: string | null;
  slug?: string | null;
};

const DEFAULT_STOREFRONT_LOCALE = "en";

function readEnv(name: string): string {
  const value =
    (import.meta as any).env?.[name] ??
    (typeof process !== "undefined" ? (process as any).env?.[name] : undefined);
  return typeof value === "string" ? value.trim() : "";
}

function readWindowLocation(): Location | null {
  return typeof window !== "undefined" ? window.location : null;
}

function isLocalHost(host?: string | null): boolean {
  const normalized = String(host || "")
    .trim()
    .toLowerCase();
  const hostname = normalized.split(":")[0];
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.endsWith(".localhost")
  );
}

function derivePlatformDomain(host?: string | null): string {
  const normalized = normalizeHost(host);
  if (!normalized) return "";
  if (normalized.startsWith("admin.")) return normalized.slice("admin.".length);
  if (isLocalHost(normalized)) return "localhost";

  const parts = normalized.split(":")[0].split(".");
  if (parts.length >= 2) {
    return parts.slice(-2).join(".");
  }

  return normalized;
}

function resolveRuntimeConfig() {
  const location = readWindowLocation();
  const storefrontOrigin = readEnv("VITE_STOREFRONT_ORIGIN");
  let storefrontUrl: URL | null = null;

  if (storefrontOrigin) {
    try {
      storefrontUrl = new URL(storefrontOrigin);
    } catch {
      storefrontUrl = null;
    }
  }

  const locationHost = location?.hostname ?? "";
  const locationProtocol = location?.protocol ?? "https:";
  const host = (storefrontUrl?.hostname || locationHost || "").toLowerCase();
  const protocol = storefrontUrl?.protocol || locationProtocol;
  const platformDomain =
    readEnv("VITE_PLATFORM_DOMAIN") ||
    readEnv("VITE_STOREFRONT_BASE_DOMAIN") ||
    derivePlatformDomain(host);
  const port = storefrontUrl?.port || (isLocalHost(host) ? "3000" : "");

  return {
    host,
    port,
    protocol,
    platformDomain,
    storefrontOrigin: storefrontUrl?.origin || storefrontOrigin,
  };
}

function normalizePath(path = ""): string {
  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
}

export function isFullyQualifiedHttpUrl(value?: string | null): boolean {
  const normalized = value?.trim();
  if (!normalized) return false;

  try {
    const parsed = new URL(normalized);
    return (
      (parsed.protocol === "http:" || parsed.protocol === "https:") &&
      Boolean(parsed.hostname)
    );
  } catch {
    return false;
  }
}

function normalizeLocale(locale?: string | null): string {
  const raw = String(locale || "")
    .trim()
    .toLowerCase()
    .replace("_", "-");
  const normalized = raw.split("-")[0] || DEFAULT_STOREFRONT_LOCALE;
  return /^[a-z]{2}$/.test(normalized) ? normalized : DEFAULT_STOREFRONT_LOCALE;
}

function normalizeHost(host?: string | null): string {
  const raw = String(host || "").trim();
  if (!raw) return "";

  try {
    const parsed = new URL(raw.includes("://") ? raw : `https://${raw}`);
    return parsed.host.toLowerCase().replace(/\.+$/, "");
  } catch {
    return raw
      .replace(/^https?:\/\//i, "")
      .replace(/^\/\//, "")
      .split("/")[0]
      .trim()
      .toLowerCase()
      .replace(/\.+$/, "");
  }
}

function hostNameOnly(host?: string | null): string {
  return normalizeHost(host).split(":")[0];
}

function normalizeStoreSlug(slug?: string | null): string {
  const normalized = String(slug || "")
    .trim()
    .toLowerCase();
  // Keep admin URL generation aligned with the backend StoreSlugRules.
  return /^[a-z]{3,40}$/.test(normalized) ? normalized : "";
}

function isSlugHost(host: string, slug: string): boolean {
  const hostname = hostNameOnly(host);
  if (!hostname || !slug) return false;
  return hostname === `${slug}.localhost` || hostname.startsWith(`${slug}.`);
}

function isManagedStorefrontHost(
  host: string,
  runtime: ReturnType<typeof resolveRuntimeConfig>,
): boolean {
  const hostname = hostNameOnly(host);
  if (!hostname) return false;
  if (isLocalHost(hostname)) return true;

  const platformDomain = hostNameOnly(runtime.platformDomain);
  if (!platformDomain || platformDomain === "localhost") return false;

  return hostname === platformDomain || hostname.endsWith(`.${platformDomain}`);
}

function isManagedHostForDifferentSlug(
  host: string,
  slug: string,
  runtime: ReturnType<typeof resolveRuntimeConfig>,
): boolean {
  return Boolean(
    slug && isManagedStorefrontHost(host, runtime) && !isSlugHost(host, slug),
  );
}

function safeEncodeSegment(value: string): string {
  try {
    return encodeURIComponent(decodeURIComponent(value));
  } catch {
    return encodeURIComponent(value);
  }
}

function getPublicStorefrontPath(locale?: string | null) {
  return `/${normalizeLocale(locale)}`;
}

function isLocaleSegment(value?: string): boolean {
  return Boolean(value && /^[a-z]{2}(?:-[a-z]{2})?$/i.test(value));
}

function shouldPreserveExplicitPath(path: string): boolean {
  return (
    path.startsWith("/api/") ||
    path.startsWith("/billing/") ||
    path.startsWith("/owner/")
  );
}

function localizePublicStorefrontPath(
  path: string,
  locale?: string | null,
): string {
  const normalizedPath = normalizePath(path);
  if (normalizedPath === "/") return getPublicStorefrontPath(locale);
  if (shouldPreserveExplicitPath(normalizedPath)) return normalizedPath;

  const firstSegment = normalizedPath.split("/").find(Boolean);
  if (isLocaleSegment(firstSegment)) return normalizedPath;

  return `${getPublicStorefrontPath(locale)}${normalizedPath}`;
}

function resolveStoreLocale(store?: PublicStorefrontStore | null) {
  return store?.locale || store?.defaultLanguage || DEFAULT_STOREFRONT_LOCALE;
}

function resolveActiveStorefrontDomain(store?: PublicStorefrontStore | null) {
  return normalizeHost(
    store?.activeDomain || store?.storefrontDomain || store?.domain,
  );
}

function hasStoreContext(options: StorefrontUrlOptions = {}): boolean {
  return Boolean(
    options.baseUrl?.trim() || options.slug?.trim() || options.domain?.trim(),
  );
}

function classifyStorefrontPath(
  path: string,
): ResolvedStorefrontTarget["kind"] {
  if (path.startsWith("/owner/billing")) return "owner-billing";
  if (path.startsWith("/billing/")) return "admin-billing";
  return "storefront";
}

function resolveTargetPath(
  options: StorefrontUrlOptions = {},
): Omit<ResolvedStorefrontTarget, "url"> {
  if (options.path?.trim()) {
    const normalizedPath = normalizePath(options.path);
    const kind = classifyStorefrontPath(normalizedPath);
    const path =
      kind === "storefront"
        ? localizePublicStorefrontPath(normalizedPath, options.locale)
        : normalizedPath;
    return {
      kind,
      path,
    };
  }

  if (!hasStoreContext(options)) {
    return {
      kind: "storefront",
      path: "/",
    };
  }

  return {
    kind: "storefront",
    path: getPublicStorefrontPath(options.locale),
  };
}

function normalizeConfiguredBaseUrl(value?: string | null): string {
  const normalized = value?.trim();
  if (!normalized) return "";

  try {
    if (normalized.startsWith("/") && !normalized.startsWith("//")) {
      return "";
    }

    const candidate = normalized.startsWith("//")
      ? `https:${normalized}`
      : /^[a-z][a-z\d+\-.]*:\/\//i.test(normalized)
        ? normalized
        : `https://${normalized}`;
    const parsed = new URL(candidate);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return "";
    }
    return parsed.origin.replace(/\/+$/, "");
  } catch {
    return "";
  }
}

function baseUrlMatchesExplicitDomain(
  baseUrl: string,
  domain: string,
): boolean {
  if (!baseUrl || !domain) return true;
  const baseHost = hostNameOnly(baseUrl);
  const domainHost = hostNameOnly(domain);
  return !baseHost || !domainHost || baseHost === domainHost;
}

function baseUrlMatchesStoreContext(
  baseUrl: string,
  domain: string,
  slug: string,
): boolean {
  return (
    baseUrlMatchesExplicitDomain(baseUrl, domain) || isSlugHost(baseUrl, slug)
  );
}

function resolveStorefrontBaseUrl(
  options: Pick<
    StorefrontUrlOptions,
    "slug" | "domain" | "path" | "baseUrl"
  > = {},
): string {
  const { slug, domain, path, baseUrl } = options;
  const hasExplicitSlug = Boolean(slug?.trim());
  const normalizedSlug = normalizeStoreSlug(slug);
  const normalizedDomain = normalizeHost(domain);
  const normalizedBaseUrl = normalizeConfiguredBaseUrl(baseUrl);

  if (hasExplicitSlug && !normalizedSlug) {
    return "";
  }

  if (!normalizedSlug && !normalizedDomain && !normalizedBaseUrl) {
    return "";
  }

  const targetPath = normalizePath(path);
  const preferSlug = targetPath.startsWith("/owner/");

  const runtime = resolveRuntimeConfig();
  const localProtocol = runtime.protocol === "http:" ? "http" : "https";
  const publicProtocol = "https";
  const slugBaseUrl =
    normalizedSlug && normalizedSlug !== "store"
      ? isLocalHost(runtime.host) || runtime.platformDomain === "localhost"
        ? `${localProtocol}://${normalizedSlug}.localhost${runtime.port ? `:${runtime.port}` : ""}`
        : runtime.platformDomain && runtime.platformDomain !== "localhost"
          ? `${publicProtocol}://${normalizedSlug}.${runtime.platformDomain}`
          : ""
      : "";

  if (preferSlug && slugBaseUrl) {
    return slugBaseUrl;
  }

  if (
    normalizedBaseUrl &&
    baseUrlMatchesStoreContext(
      normalizedBaseUrl,
      normalizedDomain,
      normalizedSlug,
    ) &&
    !isManagedHostForDifferentSlug(normalizedBaseUrl, normalizedSlug, runtime)
  ) {
    return normalizedBaseUrl;
  }

  // 1. If explicit domain is provided, use it unless it is a stale managed subdomain.
  if (
    normalizedDomain &&
    !isManagedHostForDifferentSlug(normalizedDomain, normalizedSlug, runtime)
  ) {
    const protocol = isLocalHost(normalizedDomain)
      ? localProtocol
      : publicProtocol;
    return translateUi("value0Value1InterpolatedColonSlash", "{{value0}}://{{value1}}", {
      value0: protocol,
      value1: normalizedDomain,
    });
  }

  // 2. If slug is provided, try to construct slug.platformDomain
  if (slugBaseUrl) {
    return slugBaseUrl;
  }

  // 3. Fallback to the configured storefront origin only when tenant context exists.
  if (runtime.storefrontOrigin && !isLocalHost(runtime.host)) {
    return runtime.storefrontOrigin.replace(/\/+$/, "");
  }

  // 4. Last resort: use platform domain directly
  if (runtime.platformDomain && runtime.platformDomain !== "localhost") {
    return translateUi("value0Value1InterpolatedColonSlash", "{{value0}}://{{value1}}", {
      value0: publicProtocol,
      value1: runtime.platformDomain,
    });
  }

  return "";
}

function resolvePreviewFallbackBaseUrl(): string {
  const runtime = resolveRuntimeConfig();
  if (runtime.storefrontOrigin) {
    return runtime.storefrontOrigin.replace(/\/+$/, "");
  }

  const localProtocol = runtime.protocol === "http:" ? "http" : "https";
  if (isLocalHost(runtime.host) || runtime.platformDomain === "localhost") {
    return translateUi(
      "value0LocalhostValue1",
      "{{value0}}://localhost{{value1}}",
      { value0: localProtocol, value1: runtime.port ? `:${runtime.port}` : "" },
    );
  }

  if (runtime.platformDomain && runtime.platformDomain !== "localhost") {
    return `https://${runtime.platformDomain}`;
  }

  return "";
}

export function resolveStorefrontOpenTarget(
  options: StorefrontUrlOptions = {},
): ResolvedStorefrontTarget {
  const target = resolveTargetPath(options);
  if (!options.path?.trim() && !hasStoreContext(options)) {
    return {
      ...target,
      kind: "unavailable",
      url: "",
    };
  }

  if (target.kind === "admin-billing") {
    return {
      ...target,
      url: target.path,
    };
  }

  const url = getStorefrontUrl({ ...options, path: target.path });
  return {
    ...target,
    kind: url ? target.kind : "unavailable",
    url,
  };
}

/**
 * Generates a storefront URL based on the provided options and environment configuration.
 */
export function getStorefrontUrl(options: StorefrontUrlOptions = {}): string {
  const { slug, previewToken, domain, baseUrl: configuredBaseUrl } = options;

  // 1. Identify what we are trying to point to (path and kind)
  const { kind, path } = resolveTargetPath(options);

  // 2. Short-circuit for explicit admin paths (billing)
  if (kind === "admin-billing") {
    return path;
  }

  // 3. Resolve the base URL (protocol + host)
  const baseUrl = resolveStorefrontBaseUrl({
    slug,
    domain,
    path,
    baseUrl: configuredBaseUrl,
  });
  if (!baseUrl) {
    return "";
  }

  // 4. Construct final URL, carefully merging base and path
  const origin = baseUrl.replace(/\/+$/, "");
  const normalizedPath = normalizePath(path);
  let finalUrl = `${origin}${normalizedPath}`;

  // 5. Add preview token if present
  if (previewToken && finalUrl) {
    try {
      const urlObj = new URL(finalUrl);
      urlObj.searchParams.set("token", previewToken);
      finalUrl = urlObj.toString();
    } catch {
      // Fallback if URL is somehow invalid for the URL constructor
      const separator = finalUrl.includes("?") ? "&" : "?";
      finalUrl = `${finalUrl}${separator}token=${encodeURIComponent(previewToken)}`;
    }
  }

  return isFullyQualifiedHttpUrl(finalUrl) ? finalUrl : "";
}

export function getStorefrontRootUrl(
  options: StorefrontUrlOptions = {},
): string {
  return resolveStorefrontBaseUrl({
    slug: options.slug,
    domain: options.domain,
    baseUrl: options.baseUrl,
    path: "/",
  });
}

function extractProductSlugFromPath(value: string): string | null {
  let pathname = value;
  if (/^https?:\/\//i.test(value)) {
    try {
      pathname = new URL(value).pathname;
    } catch {
      return null;
    }
  }

  const [pathWithoutQuery] = pathname.split(/[?#]/);
  const segments = pathWithoutQuery.split("/").filter(Boolean);
  if (segments.length === 0) return null;

  const routeStart = isLocaleSegment(segments[0]) ? 1 : 0;
  const route = segments[routeStart]?.toLowerCase();
  let slugSegment: string | undefined;

  if (route === "products" || route === "product") {
    slugSegment = segments[routeStart + 1];
  } else if (route === "p") {
    const next = segments[routeStart + 1]?.toLowerCase();
    slugSegment =
      next === "products" || next === "product"
        ? segments[routeStart + 2]
        : segments[routeStart + 1];
  }

  if (!slugSegment) return null;

  try {
    return decodeURIComponent(slugSegment).trim() || null;
  } catch {
    return slugSegment.trim() || null;
  }
}

function normalizeProductSlug(slug?: string | null): string | null {
  if (!slug) return null;
  const trimmed = slug.trim();
  if (!trimmed) return null;

  const extracted = extractProductSlugFromPath(trimmed);
  return extracted || trimmed;
}

function extractProductSlugFromPublicUrl(
  publicUrl?: string | null,
): string | null {
  const normalizedPublicUrl = publicUrl?.trim();
  if (!normalizedPublicUrl) return null;

  try {
    const url = new URL(normalizedPublicUrl, "https://storefront.local");
    return extractProductSlugFromPath(url.pathname);
  } catch {
    return null;
  }
}

function resolveProductSlug(
  product?: PublicStorefrontProduct | null,
): string | null {
  if (!product) return null;

  const publicUrlSlug = extractProductSlugFromPublicUrl(product.publicUrl);
  return normalizeProductSlug(product.slug) ?? publicUrlSlug;
}

function resolveCategorySlug(
  category?: PublicStorefrontCategory | null,
): string | null {
  const slug = category?.slug?.trim();
  return slug || null;
}

export function getPublicStorefrontUrl(
  store: PublicStorefrontStore = {},
): string {
  const locale = resolveStoreLocale(store);
  return getStorefrontHomeUrl(store, locale);
}

export function getStorefrontBaseUrl(store: PublicStorefrontStore): string {
  return resolveStorefrontBaseUrl({
    slug: store.slug,
    domain: resolveActiveStorefrontDomain(store),
    baseUrl: store.storefrontUrl || store.baseUrl,
  });
}

export function getStorefrontHomeUrl(
  store: PublicStorefrontStore,
  locale?: string | null,
): string {
  const effectiveLocale = locale || resolveStoreLocale(store);
  return getStorefrontUrl({
    slug: store.slug,
    domain: resolveActiveStorefrontDomain(store),
    baseUrl: store.storefrontUrl || store.baseUrl,
    locale: effectiveLocale,
    path: getPublicStorefrontPath(effectiveLocale),
  });
}

export function getStorefrontProductsUrl(
  store: PublicStorefrontStore,
  locale?: string | null,
): string {
  const effectiveLocale = locale || resolveStoreLocale(store);
  return getStorefrontUrl({
    slug: store.slug,
    domain: resolveActiveStorefrontDomain(store),
    baseUrl: store.storefrontUrl || store.baseUrl,
    locale: effectiveLocale,
    path: `/${normalizeLocale(effectiveLocale)}/products`,
  });
}

export function getStorefrontProductUrl(
  store: PublicStorefrontStore,
  locale?: string | null,
  productSlug?: string | null,
): string {
  const resolvedProductSlug = normalizeProductSlug(productSlug);
  if (!resolvedProductSlug) return "";

  const effectiveLocale = locale || resolveStoreLocale(store);
  return getStorefrontUrl({
    slug: store.slug,
    domain: resolveActiveStorefrontDomain(store),
    baseUrl: store.storefrontUrl || store.baseUrl,
    locale: effectiveLocale,
    path: `/${normalizeLocale(effectiveLocale)}/products/${safeEncodeSegment(resolvedProductSlug)}`,
  });
}

export function getPublicProductUrl(
  store: PublicStorefrontStore,
  product: PublicStorefrontProduct,
): string {
  const productSlug = resolveProductSlug(product);
  if (!productSlug) return "";

  const locale = resolveStoreLocale(store);
  return getStorefrontProductUrl(store, locale, productSlug);
}

export function getPublicCollectionUrl(
  store: PublicStorefrontStore,
  category: PublicStorefrontCategory,
): string {
  const categorySlug = resolveCategorySlug(category);
  if (!categorySlug) return "";

  const locale = resolveStoreLocale(store);
  const url = getStorefrontUrl({
    slug: store.slug,
    domain: resolveActiveStorefrontDomain(store),
    baseUrl: store.storefrontUrl || store.baseUrl,
    locale,
    path: `/${normalizeLocale(locale)}/products`,
  });
  if (!url) return "";

  const nextUrl = new URL(url);
  nextUrl.searchParams.set("category", categorySlug);
  return nextUrl.toString();
}

export function getStorefrontPreviewUrl(
  options: StorefrontPreviewUrlOptions,
): string {
  const {
    previewToken,
    redirectPath = "/",
    origin,
    slug,
    domain,
    locale,
  } = options;
  const baseUrl =
    normalizeConfiguredBaseUrl(origin) ||
    resolveStorefrontBaseUrl({ slug, domain }) ||
    resolvePreviewFallbackBaseUrl();
  if (!baseUrl) return "";
  const redirect = localizePublicStorefrontPath(redirectPath, locale);
  const url = new URL("/api/preview", `${baseUrl.replace(/\/+$/, "")}/`);
  url.searchParams.set("token", previewToken);
  url.searchParams.set("redirect", redirect);
  return url.toString();
}

/**
 * Opens a storefront URL in a new tab.
 */
export function openStorefront(
  options: StorefrontUrlOptions = {},
): Window | null {
  const target = resolveStorefrontOpenTarget(options);
  if (target.kind !== "storefront" || !isFullyQualifiedHttpUrl(target.url)) {
    return null;
  }
  return window.open(target.url, "_blank", "noopener,noreferrer");
}
